"use client"

import { useState, useEffect, useRef } from "react"
import PlaceCard from "./components/PlaceCard"
import "./App.css"
import { User, Share2, Trash2, ArrowLeft, RotateCcw, Eye, EyeOff, Crown, RefreshCw } from 'lucide-react'
import NicknameModal from "./components/NicknameModal"
import PasswordModal from "./components/PasswordModal"
import MapView from "./components/MapView"
import { Map, Menu, MapPin } from 'lucide-react'
import TypeFilter from "./components/TypeFilter"
import VoteRecoveryModal from "./components/VoteRecoveryModal"
import PlanningModal from "./components/PlanningModal"
import PlanningInterface from "./components/PlanningInterface"

function App() {
  const [showVoteRecoveryModal, setShowVoteRecoveryModal] = useState(false)
  const [isRecoveryLoading, setIsRecoveryLoading] = useState(false)
  const [places, setPlaces] = useState([])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [url, setUrl] = useState("")
  const [likedPlaces, setLikedPlaces] = useState([])
  const [currentView, setCurrentView] = useState("input") // Add "planning" as a new view option
  const [allVotes, setAllVotes] = useState({})
  const [myVotes, setMyVotes] = useState({})
  const [isSharedList, setIsSharedList] = useState(false)
  const [userId, setUserId] = useState("")
  const [savedLists, setSavedLists] = useState([])
  const [currentListInfo, setCurrentListInfo] = useState(null)
  const [showVotes, setShowVotes] = useState(false)
  const [userNickname, setUserNickname] = useState("")
  const [currentListId, setCurrentListId] = useState(null)
  const [isVoteSyncing, setIsVoteSyncing] = useState(false)
  const [lastSyncTime, setLastSyncTime] = useState(null)
  const [isCreator, setIsCreator] = useState(false)
  const [mySharedLists, setMySharedLists] = useState([])
  const [showManageMenu, setShowManageMenu] = useState(false)
  const [originalUrl, setOriginalUrl] = useState("")
  const [filteredPlaces, setFilteredPlaces] = useState([])
  const [typeFilter, setTypeFilter] = useState("all")
  const [placeTypes, setPlaceTypes] = useState({})
  const [allNotes, setAllNotes] = useState({})
  const [myNotes, setMyNotes] = useState({})
  const [showPlanningModal, setShowPlanningModal] = useState(false)
  const [savedMapState, setSavedMapState] = useState(null)

  // Add trip planning state
  const [linkedTripData, setLinkedTripData] = useState(null)
  const [hasLinkedTrip, setHasLinkedTrip] = useState(false)
  const [sharedPlanId, setSharedPlanId] = useState(null)

  // Modal states
  const [showNicknameModal, setShowNicknameModal] = useState(false)
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [passwordError, setPasswordError] = useState(null)
  const [isPasswordLoading, setIsPasswordLoading] = useState(false)

  // Refs for polling
  const pollIntervalRef = useRef(null)
  const isPollingRef = useRef(false)

  // Add this near the other refs at the top of the App component
  const updateRequestRef = useRef(null)
  const debounceTimeoutRef = useRef(null)

  // API base URL - update this to your domain
  const API_BASE = "https://triptoi.com/api"

  // Load linked trip data when currentListId changes
  useEffect(() => {
    if (currentListId && isSharedList) {
      loadLinkedTripData()
    } else {
      setLinkedTripData(null)
      setHasLinkedTrip(false)
      setSharedPlanId(null)
    }
  }, [currentListId, isSharedList])

  // Load linked trip data from server and localStorage
  const loadLinkedTripData = async () => {
    console.log("🔗 Loading linked trip data for shared list:", currentListId)

    if (!currentListId || !isSharedList) {
      console.log("❌ No shared list ID, skipping trip data load")
      return
    }

    try {
      // First check if there's a linked plan on the server
      const response = await fetch(`${API_BASE}/trip-manager.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "get-linked-plan",
          listId: currentListId,
        }),
      })
      const data = await response.json()

      if (data.success && data.hasLinkedPlan && data.tripData) {
        console.log("📅 Found linked trip plan on server:", data.tripData)
        setLinkedTripData(data.tripData)
        setHasLinkedTrip(true)
        setSharedPlanId(data.planId)

        // Also save to localStorage for offline access
        const localStorageKey = `tripCalendarData_${currentListId}`
        localStorage.setItem(localStorageKey, JSON.stringify(data.tripData))
        localStorage.setItem("tripCalendarData_v3", JSON.stringify(data.tripData))

        return
      }

      // Fallback to localStorage if no server plan
      const localStorageKey = `tripCalendarData_${currentListId}`
      const localData = localStorage.getItem(localStorageKey)
      const mainLocalData = localStorage.getItem("tripCalendarData_v3")

      let foundLocalData = null

      if (localData) {
        try {
          foundLocalData = JSON.parse(localData)
          console.log("📅 Found list-specific local trip data:", foundLocalData)
        } catch (error) {
          console.error("Error parsing list-specific trip data:", error)
        }
      }

      if (!foundLocalData && mainLocalData) {
        try {
          foundLocalData = JSON.parse(mainLocalData)
          console.log("📅 Found main local trip data:", foundLocalData)
        } catch (error) {
          console.error("Error parsing main trip data:", error)
        }
      }

      if (foundLocalData && foundLocalData.itinerary && foundLocalData.itinerary.length > 0) {
        console.log("📅 Using local trip data")
        setLinkedTripData(foundLocalData)
        setHasLinkedTrip(true)
        return
      }

      // No trip data found anywhere
      console.log("📅 No trip data found")
      setLinkedTripData(null)
      setHasLinkedTrip(false)
      setSharedPlanId(null)
    } catch (error) {
      console.error("Error loading linked trip data:", error)
      setLinkedTripData(null)
      setHasLinkedTrip(false)
      setSharedPlanId(null)
    }
  }

  // Save linked trip data to both localStorage and server
  const saveLinkedTripData = async (tripData) => {
    if (!currentListId || !isSharedList) {
      console.warn("No shared list to save trip data to")
      return
    }

    console.log("💾 Saving linked trip data for shared list:", currentListId)

    try {
      // Save to localStorage first for immediate UI update
      const localStorageKey = `tripCalendarData_${currentListId}`
      const tripDataStr = JSON.stringify(tripData)
      localStorage.setItem(localStorageKey, tripDataStr)
      localStorage.setItem("tripCalendarData_v3", tripDataStr)

      setLinkedTripData(tripData)
      setHasLinkedTrip(true)

      console.log("✅ Trip data saved to localStorage")

      // Save to server to create/update shared plan
      if (API_BASE) {
        try {
          const response = await fetch(`${API_BASE}/trip-manager.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action: "save",
              listId: currentListId,
              userId: userId,
              tripData: tripData,
            }),
          })

          const data = await response.json()
          if (data.success) {
            console.log("✅ Trip data saved as shared plan and linked to list")
            setSharedPlanId(data.planId)

            // Update trip data with shared plan info
            const updatedTripData = {
              ...tripData,
              sharedPlanId: data.planId,
              isSharedPlan: true,
            }
            setLinkedTripData(updatedTripData)

            // Update localStorage with shared plan info
            localStorage.setItem(localStorageKey, JSON.stringify(updatedTripData))
            localStorage.setItem("tripCalendarData_v3", JSON.stringify(updatedTripData))
          } else {
            console.warn("⚠️ Server save failed, but local save succeeded:", data.error)
          }
        } catch (serverError) {
          console.warn("⚠️ Server save failed, but local save succeeded:", serverError)
        }
      }
    } catch (error) {
      console.error("❌ Error saving trip data:", error)
    }
  }

  // Update trip data for shared plans
  const updateSharedTripData = async (tripData) => {
    if (!sharedPlanId) {
      console.log("📅 No shared plan ID, saving as new linked plan")
      return saveLinkedTripData(tripData)
    }

    console.log("🔄 Updating shared trip plan:", sharedPlanId)

    try {
      // Update localStorage immediately
      const localStorageKey = `tripCalendarData_${currentListId}`
      const tripDataStr = JSON.stringify(tripData)
      localStorage.setItem(localStorageKey, tripDataStr)
      localStorage.setItem("tripCalendarData_v3", tripDataStr)

      setLinkedTripData(tripData)

      // Update server
      if (API_BASE) {
        const response = await fetch(`${API_BASE}/trip-manager.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            action: "update",
            planId: sharedPlanId,
            userId: userId,
            tripData: tripData,
          }),
        })

        const data = await response.json()
        if (data.success) {
          console.log("✅ Shared trip plan updated successfully")
        } else {
          console.warn("⚠️ Server update failed:", data.error)
        }
      }
    } catch (error) {
      console.error("❌ Error updating shared trip data:", error)
    }
  }

  // Debug logging for planning modal state
  useEffect(() => {
    console.log("🎯 Planning Modal State Changed:", {
      showPlanningModal,
      currentView,
      placesCount: places.length,
      hasLinkedTrip,
      isSharedList,
      sharedPlanId,
      timestamp: new Date().toISOString(),
    })
  }, [showPlanningModal, currentView, places.length, hasLinkedTrip, isSharedList, sharedPlanId])

  // Calendar button click handler with logging
  const handlePlanningModalOpen = async () => {
    console.log("📅 Calendar button clicked!")
    console.log("📅 Current state:", {
      showPlanningModal,
      currentView,
      placesCount: places.length,
      userId,
      currentListId,
      hasLinkedTrip,
      isSharedList,
      sharedPlanId,
    })

    try {
      // Force reload trip data before deciding what to show
      console.log("📅 Force loading trip data before showing modal...")
      await loadLinkedTripData()

      // Small delay to ensure state is updated
      setTimeout(() => {
        // If user already has trip data, skip modal and go directly to planning view
        if (linkedTripData && linkedTripData.itinerary && linkedTripData.itinerary.length > 0) {
          console.log("📅 User has existing trip data, going directly to planning view")
          setCurrentView("planning")
        } else {
          console.log("📅 No existing trip data, showing planning modal")
          setShowPlanningModal(true)
        }
      }, 100)
    } catch (error) {
      console.error("❌ Error loading trip data before showing modal:", error)
      // If there's an error loading, show the modal as fallback
      setShowPlanningModal(true)
    }
  }

  // Planning modal close handler with logging
  const handlePlanningModalClose = () => {
    console.log("📅 Planning modal close requested")
    try {
      setShowPlanningModal(false)
      console.log("📅 Planning modal closed successfully")
    } catch (error) {
      console.error("❌ Error closing planning modal:", error)
    }
  }

  // Add this function after the existing handlers
  const handleSwitchToPlanningView = (tripData) => {
    console.log("🎯 Switching to planning view with trip data:", tripData)

    // Save the trip data as linked to this shared list
    if (tripData && currentListId && isSharedList) {
      saveLinkedTripData(tripData)
    } else if (tripData) {
      // For non-shared lists, just save locally
      const localStorageKey = currentListId ? `tripCalendarData_${currentListId}` : "tripCalendarData_v3"
      localStorage.setItem(localStorageKey, JSON.stringify(tripData))
      localStorage.setItem("tripCalendarData_v3", JSON.stringify(tripData))
      setLinkedTripData(tripData)
      setHasLinkedTrip(true)
    }

    // Close the modal first
    setShowPlanningModal(false)

    // Then switch to planning view
    setTimeout(() => {
      setCurrentView("planning")
    }, 100)
  }

  // Add this function after the existing handlers
  const handleBackFromPlanning = () => {
    console.log("🔙 Returning from planning view to voting")
    setCurrentView("swipe")
  }

  // Filter places by type
  const getFilteredPlaces = (placesToFilter) => {
    if (typeFilter === "all" || !typeFilter) {
      return placesToFilter
    }

    return placesToFilter.filter((place) => {
      const key = `${place.lat},${place.lng}`
      const placeType = placeTypes[key] || place.type
      return placeType === typeFilter
    })
  }

  // Handle map state changes
  const handleMapStateChange = (mapState) => {
    setSavedMapState(mapState)
    localStorage.setItem("triptoi-map-state", JSON.stringify(mapState))
  }

  // Load saved map state on app start
  useEffect(() => {
    const savedState = localStorage.getItem("triptoi-map-state")
    if (savedState) {
      try {
        setSavedMapState(JSON.parse(savedState))
      } catch (error) {
        console.error("Error loading saved map state:", error)
      }
    }
  }, [])

  // Update place type function
  const updatePlaceType = (placeKey, type) => {
    console.log("Updating place type:", placeKey, type)

    setPlaceTypes((prev) => ({
      ...prev,
      [placeKey]: type,
    }))

    const savedTypes = JSON.parse(localStorage.getItem("triptoi-place-types") || "{}")
    if (type) {
      savedTypes[placeKey] = type
    } else {
      delete savedTypes[placeKey]
    }
    localStorage.setItem("triptoi-place-types", JSON.stringify(savedTypes))

    console.log("Place types updated:", savedTypes)
  }

  // Generate or get user ID and nickname
  useEffect(() => {
    let storedUserId = localStorage.getItem("triptoi-user-id")
    if (!storedUserId) {
      storedUserId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      localStorage.setItem("triptoi-user-id", storedUserId)
    }
    setUserId(storedUserId)
    console.log("🆔 User ID set:", storedUserId) // Debug log

    const storedNickname = localStorage.getItem("triptoi-nickname")
    if (storedNickname) {
      setUserNickname(storedNickname)
    }

    const savedShowVotes = localStorage.getItem("triptoi-show-votes")
    if (savedShowVotes !== null) {
      setShowVotes(JSON.parse(savedShowVotes))
    }
  }, [])

  // Load saved place types from localStorage
  useEffect(() => {
    const loadSavedTypes = () => {
      try {
        const saved = localStorage.getItem("triptoi-place-types")
        console.log("🏷️ Loading saved place types:", saved) // Debug log
        if (saved) {
          const types = JSON.parse(saved)
          console.log("🏷️ Parsed place types:", types) // Debug log
          setPlaceTypes(types)
        }
      } catch (error) {
        console.error("Error loading saved place types:", error)
      }
    }
    loadSavedTypes()
  }, [])

  // Handle vote recovery
  const handleRecoverVotes = async (targetNickname) => {
    if (!currentListId) {
      alert("No active list to recover votes for")
      return
    }

    setIsRecoveryLoading(true)

    try {
      // Find the original userId for this nickname
      let originalUserId = null
      Object.values(allVotes).forEach((voteData) => {
        if (voteData.userDetails) {
          const userDetail = voteData.userDetails.find((detail) => detail.nickname === targetNickname)
          if (userDetail) {
            originalUserId = userDetail.userId
          }
        }
      })

      if (!originalUserId) {
        throw new Error("Could not find original user ID")
      }

      // Call API to transfer votes
      const response = await fetch(`${API_BASE}/list-manager.php?action=transfer-votes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          listId: currentListId,
          fromUserId: originalUserId,
          toUserId: userId,
          nickname: targetNickname,
        }),
      })

      const data = await response.json()

      if (data.success) {
        // Update local state
        setUserNickname(targetNickname)
        localStorage.setItem("triptoi-nickname", targetNickname)

        // Rebuild votes with new userId
        const newAllVotes = { ...allVotes }
        const newMyVotes = {}

        Object.entries(newAllVotes).forEach(([placeKey, voteData]) => {
          if (voteData.userDetails) {
            voteData.userDetails = voteData.userDetails.map((detail) => {
              if (detail.userId === originalUserId) {
                newMyVotes[placeKey] = { vote: detail.vote, userId: userId, timestamp: Date.now() }
                return { ...detail, userId: userId }
              }
              return detail
            })
          }
        })

        setAllVotes(newAllVotes)
        setMyVotes(newMyVotes)
        setShowVoteRecoveryModal(false)

        alert(
          `✅ Successfully recovered your votes as "${targetNickname}"!\n\nYou can now continue voting from this device.`,
        )
      } else {
        throw new Error(data.error || "Failed to recover votes")
      }
    } catch (error) {
      console.error("Error recovering votes:", error)
      alert("Error recovering votes: " + error.message)
    } finally {
      setIsRecoveryLoading(false)
    }
  }

  // Real-time polling for shared lists
  useEffect(() => {
    if (currentListId && isSharedList) {
      console.log("🔄 Starting real-time polling for list:", currentListId)
      startPolling()
    } else {
      console.log("⏹️ Stopping real-time polling")
      stopPolling()
    }

    return () => stopPolling()
  }, [currentListId, isSharedList])

  const startPolling = () => {
    if (isPollingRef.current) return

    isPollingRef.current = true
    pollIntervalRef.current = setInterval(() => {
      if (currentListId && !isVoteSyncing) {
        fetchLatestVotes()
        // Also check for trip plan updates
        if (hasLinkedTrip) {
          loadLinkedTripData()
        }
      }
    }, 5000) // Poll every 5 seconds
  }

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
    isPollingRef.current = false
  }

  const fetchLatestVotes = async () => {
    if (!currentListId) return

    try {
      const response = await fetch(`${API_BASE}/list-manager.php?id=${currentListId}`)
      const data = await response.json()

      if (data.success) {
        const serverVotes = data.votes
        const updatedVotes = { ...serverVotes }

        // Preserve local votes
        Object.entries(myVotes).forEach(([placeKey, myVote]) => {
          if (updatedVotes[placeKey] && updatedVotes[placeKey].users) {
            updatedVotes[placeKey].users[userId] = myVote.vote
          }
        })

        // Only update if votes actually changed to prevent unnecessary re-renders
        const currentVotesString = JSON.stringify(allVotes)
        const newVotesString = JSON.stringify(updatedVotes)

        if (currentVotesString !== newVotesString) {
          setAllVotes(updatedVotes)
          setLastSyncTime(Date.now())
          console.log("🔄 Synced latest votes from server")
        }
      }
    } catch (error) {
      console.error("Error fetching latest votes:", error)
    }
  }

  // Load saved lists from localStorage
  useEffect(() => {
    const loadSavedLists = () => {
      try {
        const saved = localStorage.getItem("triptoi-saved-lists")
        if (saved) {
          const lists = JSON.parse(saved)
          setSavedLists(lists)
        }
      } catch (error) {
        console.error("Error loading saved lists:", error)
      }
    }
    loadSavedLists()
  }, [])

  // Load saved shared lists from localStorage
  useEffect(() => {
    const loadMySharedLists = () => {
      try {
        const saved = localStorage.getItem("triptoi-my-shared-lists")
        if (saved) {
          const lists = JSON.parse(saved)
          setMySharedLists(lists)
        }
      } catch (error) {
        console.error("Error loading my shared lists:", error)
      }
    }
    loadMySharedLists()
  }, [])

  // Prevent body scroll on iOS when in swipe view
  useEffect(() => {
    if (currentView === "swipe") {
      document.body.classList.add("swipe-view-active")
    } else {
      document.body.classList.remove("swipe-view-active")
    }

    return () => {
      document.body.classList.remove("swipe-view-active")
    }
  }, [currentView])

  // Helper function to restore user's votes from shared data
  const restoreUserVotes = (votesData, userId) => {
    const userVotes = {}

    Object.entries(votesData).forEach(([placeKey, voteInfo]) => {
      if (voteInfo.users && voteInfo.users[userId]) {
        userVotes[placeKey] = {
          vote: voteInfo.users[userId],
          userId: userId,
          timestamp: Date.now(),
        }
      }
    })

    return userVotes
  }

  // Check for shared list in URL
  useEffect(() => {
    const checkForSharedList = () => {
      const urlParams = new URLSearchParams(window.location.search)
      const shareId = urlParams.get("share")
      const sharedData = urlParams.get("list")

      console.log("🔍 URL params:", { shareId, sharedData })
      console.log("🔍 Current URL:", window.location.href)

      if (shareId && userId) {
        console.log("🔍 Attempting to load shared list:", shareId)
        loadSharedList(shareId)
      } else if (sharedData) {
        try {
          console.log("🔗 Loading legacy shared list from URL...")
          const decoded = JSON.parse(atob(sharedData.replace(/-/g, "+").replace(/_/g, "/")))

          if (decoded.places && decoded.places.length > 0) {
            setPlaces(decoded.places)
            setAllVotes(decoded.votes || {})
            setIsSharedList(true)
            setCurrentView("swipe")
            setCurrentIndex(0)

            const restoredVotes = restoreUserVotes(decoded.votes || {}, userId)
            setMyVotes(restoredVotes)
            setLikedPlaces([])

            const listInfo = {
              id: `shared_${Date.now()}`,
              title: `Shared List (${decoded.places.length} places)`,
              places: decoded.places,
              votes: decoded.votes || {},
              isShared: true,
              lastAccessed: Date.now(),
              preview: decoded.places
                .slice(0, 3)
                .map((p) => p.name)
                .join(", "),
              shareUrl: window.location.href,
            }
            setCurrentListInfo(listInfo)
            saveListToRecents(listInfo)

            console.log(`✅ Loaded legacy shared list: ${decoded.places.length} places`)
          }
        } catch (error) {
          console.error("❌ Error loading legacy shared list:", error)
          alert("Could not load shared list. The link may be corrupted.")
          window.history.replaceState({}, document.title, window.location.pathname)
        }
      } else {
        console.log("❌ No share parameters found in URL")
      }
    }

    if (userId) {
      checkForSharedList()
    }
  }, [userId])

  // Reset currentIndex when filter changes if it's out of bounds
  useEffect(() => {
    const filteredPlaces = getFilteredPlaces(places)
    if (currentIndex >= filteredPlaces.length && filteredPlaces.length > 0) {
      setCurrentIndex(0)
    }
  }, [typeFilter, places, currentIndex])

  const loadSharedList = async (listId, password) => {
    try {
      setIsLoading(true)
      console.log("🔍 Loading shared list:", listId, password ? "with password" : "no password")
      console.log(
        "🔍 Full URL being called:",
        `${API_BASE}/list-manager.php?id=${listId}${password ? `&password=${encodeURIComponent(password)}` : ""}`,
      )

      const url = `${API_BASE}/list-manager.php?id=${listId}${password ? `&password=${encodeURIComponent(password)}` : ""}`

      const response = await fetch(url)
      console.log("📥 Response status:", response.status, response.statusText)

      const data = await response.json()
      console.log("📥 Server response:", data)

      if (data.success) {
        setPlaces(data.list.places)
        setAllVotes(data.votes)
        // Load place types from server if available
        if (data.placeTypes) {
          console.log("🏷️ Loading place types from server:", data.placeTypes)
          setPlaceTypes(data.placeTypes)
          // Also save to localStorage for offline use
          localStorage.setItem("triptoi-place-types", JSON.stringify(data.placeTypes))
        } else {
          console.log("🏷️ No place types found on server, using localStorage")
          // Fallback to localStorage if server doesn't have place types
          const savedTypes = localStorage.getItem("triptoi-place-types")
          if (savedTypes) {
            try {
              const types = JSON.parse(savedTypes)
              setPlaceTypes(types)
            } catch (error) {
              console.error("Error loading saved place types:", error)
              setPlaceTypes({})
            }
          } else {
            setPlaceTypes({})
          }
        }
        setCurrentListId(listId)
        setIsSharedList(true)
        setCurrentView("map")
        setCurrentIndex(0)
        setOriginalUrl(data.list.originalUrl || "")

        setIsCreator(data.list.creatorUserId === userId)

        const userVotes = {}
        Object.entries(data.votes).forEach(([placeKey, voteInfo]) => {
          if (voteInfo.users && voteInfo.users[userId]) {
            userVotes[placeKey] = {
              vote: voteInfo.users[userId],
              userId: userId,
              timestamp: Date.now(),
            }
          }
        })
        setMyVotes(userVotes)

        const needsNickname = !userNickname || userNickname.startsWith("User ") || userNickname.length < 2
        if (needsNickname) {
          setTimeout(() => {
            setShowNicknameModal(true)
          }, 500)
        }

        setShowPasswordModal(false)
        setPasswordError(null)
        setLastSyncTime(Date.now())

        console.log("✅ Successfully loaded shared list")

        // After loading the shared list, check for linked trip plan
        setTimeout(() => {
          loadLinkedTripData()
        }, 1000)
      } else if (data.requiresPassword) {
        setCurrentView("map") // Switch to map view even when password required
        setShowPasswordModal(true)
        setPasswordError(password ? "Incorrect password" : null)
      } else {
        throw new Error(data.error || "Failed to load list")
      }
    } catch (error) {
      console.error("❌ Error loading shared list:", error)
      alert("Could not load shared list: " + error.message + "\n\nPlease check if the link is correct and try again.")
    } finally {
      setIsLoading(false)
      setIsPasswordLoading(false)
    }
  }

  const handlePasswordSubmit = (password) => {
    setIsPasswordLoading(true)
    setPasswordError(null)

    const urlParams = new URLSearchParams(window.location.search)
    const shareId = urlParams.get("share")

    if (shareId) {
      loadSharedList(shareId, password)
    }
  }

  const saveUserNickname = async (nickname) => {
    try {
      const response = await fetch(`${API_BASE}/list-manager.php?action=set-nickname`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, nickname }),
      })

      const data = await response.json()
      if (data.success) {
        setUserNickname(nickname)
        localStorage.setItem("triptoi-nickname", nickname)
      } else {
        throw new Error(data.error || "Failed to save nickname")
      }
    } catch (error) {
      console.error("Error saving nickname:", error)
      throw error
    }
  }

  // Handle adding a new location
  const handleAddLocation = async (url) => {
    if (!currentListId) {
      throw new Error("No active list to add location to")
    }

    try {
      console.log("🔄 Adding new location from URL:", url)

      // Parse the URL to get place data
      const parseResponse = await fetch("/api/parse-maps.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ url: url.trim() }),
      })

      const parseData = await parseResponse.json()

      if (!parseData.success || !parseData.places.length) {
        throw new Error("Could not parse the location URL or no place found")
      }

      const newPlace = parseData.places[0] // Take the first place from the parsed result

      // Add to database
      const addResponse = await fetch(`${API_BASE}/list-manager.php?action=add-place`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          listId: currentListId,
          place: {
            id: newPlace.id,
            name: newPlace.name.replace(/[^\w\s-]/g, ""),
            lat: Number(newPlace.lat),
            lng: Number(newPlace.lng),
            isManuallyAdded: true, // Flag to prevent deletion during refresh
          },
          userId: userId,
        }),
      })

      const addData = await addResponse.json()

      if (addData.success) {
        // Update local state
        const updatedPlace = {
          id: newPlace.id,
          name: newPlace.name.replace(/[^\w\s-]/g, ""),
          lat: Number(newPlace.lat),
          lng: Number(newPlace.lng),
          isManuallyAdded: true,
        }

        setPlaces((prev) => [...prev, updatedPlace])

        // Update current list info
        if (currentListInfo) {
          const updatedListInfo = {
            ...currentListInfo,
            places: [...currentListInfo.places, updatedPlace],
            lastAccessed: Date.now(),
          }
          setCurrentListInfo(updatedListInfo)
          saveListToRecents(updatedListInfo)
        }

        console.log("✅ Successfully added new location:", newPlace.name)
      } else {
        throw new Error(addData.error || "Failed to add location")
      }
    } catch (error) {
      console.error("❌ Error adding location:", error)
      throw error
    }
  }

  // Refresh list from original URL
  const handleRefreshFromUrl = async () => {
    // Check multiple sources for the original URL
    let urlToUse = originalUrl

    // If no originalUrl in state, check if currentListInfo has it
    if (!urlToUse && currentListInfo?.originalUrl) {
      urlToUse = currentListInfo.originalUrl
    }

    // If still no URL, check localStorage for recent lists
    if (!urlToUse && currentListId) {
      const saved = localStorage.getItem("triptoi-saved-lists")
      if (saved) {
        const lists = JSON.parse(saved)
        const currentList = lists.find((list) => list.id === currentListId)
        if (currentList?.originalUrl) {
          urlToUse = currentList.originalUrl
        }
      }
    }

    if (!urlToUse) {
      alert(
        "No original URL found for this list. This feature is only available for lists created from Google Maps URLs.\n\nTo use this feature:\n1. Create a new list from a Google Maps URL\n2. Or ask the list creator to refresh from their device",
      )
      return
    }

    const confirmed = window.confirm(
      `🔄 Refresh list from original Google Maps URL?\n\nThis will:\n• Add any new places from the URL\n• Remove places no longer in the URL\n• Keep votes for places that remain\n• Delete votes for removed places\n\nOriginal URL: ${urlToUse.substring(0, 100)}...`,
    )

    if (!confirmed) return

    try {
      setIsLoading(true)

      const response = await fetch("/api/parse-maps.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ url: urlToUse }),
      })

      const parseData = await response.json()

      if (!parseData.success || !parseData.places.length) {
        throw new Error("Could not parse the original URL or no places found")
      }

      // Load saved edits to preserve custom names
      const savedEdits = JSON.parse(localStorage.getItem("triptoi-edits") || "{}")

      const cleanPlaces = parseData.places.map((place) => {
        const placeKey = `${place.lat},${place.lng}`

        // Check if this place already exists and has been edited
        const editedName = savedEdits[placeKey]

        // Use edited name if it exists, otherwise use the original name from URL
        const finalName = editedName || place.name.replace(/[^\w\s-]/g, "")

        return {
          id: place.id,
          name: finalName,
          lat: Number(place.lat),
          lng: Number(place.lng),
        }
      })

      const updateResponse = await fetch(`${API_BASE}/list-manager.php?action=refresh-list`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          listId: currentListId,
          userId: userId,
          places: cleanPlaces,
        }),
      })

      const updateData = await updateResponse.json()

      if (updateData.success) {
        // Get manually added places that should be preserved
        const manuallyAddedPlaces = places.filter((place) => place.isManuallyAdded)

        // Combine original URL places with manually added places
        const allPlaces = [...cleanPlaces, ...manuallyAddedPlaces]
        setPlaces(allPlaces)

        const newPlaceKeys = allPlaces.map((place) => `${place.lat},${place.lng}`)
        const filteredVotes = {}
        const filteredMyVotes = {}

        Object.entries(allVotes).forEach(([placeKey, voteData]) => {
          if (newPlaceKeys.includes(placeKey)) {
            filteredVotes[placeKey] = voteData
          }
        })

        Object.entries(myVotes).forEach(([placeKey, voteData]) => {
          if (newPlaceKeys.includes(placeKey)) {
            filteredMyVotes[placeKey] = voteData
          }
        })

        setAllVotes(filteredVotes)
        setMyVotes(filteredMyVotes)

        if (currentIndex >= allPlaces.length) {
          setCurrentIndex(0)
        }

        if (!originalUrl && urlToUse) {
          setOriginalUrl(urlToUse)
        }

        alert(
          `✅ List refreshed successfully!\n\n📍 Updated to ${allPlaces.length} places (${cleanPlaces.length} from URL + ${manuallyAddedPlaces.length} manually added)\n🗳️ Votes preserved where possible`,
        )
        console.log("🔄 List refreshed from original URL")
      } else {
        throw new Error(updateData.error || "Failed to refresh list")
      }
    } catch (error) {
      console.error("Error refreshing list:", error)
      alert("Error refreshing list: " + error.message)
    } finally {
      setIsLoading(false)
    }
  }

  // Clear all votes (creator only)
  const handleClearVotes = async () => {
    if (!isCreator || !currentListId) {
      alert("Only the list creator can clear votes")
      return
    }

    const confirmed = window.confirm(
      "⚠️ Are you sure you want to clear ALL votes?\n\nThis will permanently delete all votes from everyone and cannot be undone!",
    )

    if (!confirmed) return

    try {
      setIsLoading(true)

      const response = await fetch(`${API_BASE}/list-manager.php?action=clear-votes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          listId: currentListId,
          userId: userId,
        }),
      })

      const data = await response.json()

      if (data.success) {
        setAllVotes({})
        setMyVotes({})
        setLikedPlaces([])

        alert("✅ All votes have been cleared!")
        console.log("🗑️ All votes cleared by creator")
      } else {
        throw new Error(data.error || "Failed to clear votes")
      }
    } catch (error) {
      console.error("Error clearing votes:", error)
      alert("Error clearing votes: " + error.message)
    } finally {
      setIsLoading(false)
    }
  }

  const saveListToRecents = (listInfo) => {
    try {
      const saved = localStorage.getItem("triptoi-saved-lists")
      let lists = saved ? JSON.parse(saved) : []

      // Ensure originalUrl is preserved
      const listToSave = {
        ...listInfo,
        originalUrl: listInfo.originalUrl || originalUrl || "",
      }

      lists = lists.filter((list) => list.shareUrl !== listToSave.shareUrl && list.title !== listToSave.title)
      lists.unshift(listToSave)
      lists = lists.slice(0, 10)

      localStorage.setItem("triptoi-saved-lists", JSON.stringify(lists))
      setSavedLists(lists)
    } catch (error) {
      console.error("Error saving list:", error)
    }
  }

  const saveSharedList = (listData) => {
    try {
      const saved = localStorage.getItem("triptoi-my-shared-lists")
      let lists = saved ? JSON.parse(saved) : []

      lists = lists.filter((list) => list.id !== listData.id)
      lists.unshift(listData)
      lists = lists.slice(0, 20)

      localStorage.setItem("triptoi-my-shared-lists", JSON.stringify(lists))
      setMySharedLists(lists)
    } catch (error) {
      console.error("Error saving shared list:", error)
    }
  }

  const loadSavedList = (listInfo) => {
    try {
      setPlaces(listInfo.places)
      setAllVotes(listInfo.votes || {})
      setIsSharedList(listInfo.isShared || false)
      setCurrentView("map")
      setCurrentIndex(0)

      const restoredVotes = restoreUserVotes(listInfo.votes || {}, userId)
      setMyVotes(restoredVotes)
      setLikedPlaces([])
      setCurrentListInfo(listInfo)

      const updatedListInfo = { ...listInfo, lastAccessed: Date.now() }
      saveListToRecents(updatedListInfo)

      console.log(`📂 Loaded saved list: ${listInfo.title}`)
    } catch (error) {
      console.error("Error loading saved list:", error)
      alert("Could not load saved list")
    }
  }

  const loadMySharedList = (sharedList) => {
    window.location.href = sharedList.url
  }

  const deleteSavedList = (listId) => {
    try {
      const saved = localStorage.getItem("triptoi-saved-lists")
      if (saved) {
        let lists = JSON.parse(saved)
        lists = lists.filter((list) => list.id !== listId)
        localStorage.setItem("triptoi-saved-lists", JSON.stringify(lists))
        setSavedLists(lists)
      }
    } catch (error) {
      console.error("Error deleting saved list:", error)
    }
  }

  const deleteMySharedList = (listId) => {
    try {
      const saved = localStorage.getItem("triptoi-my-shared-lists")
      if (saved) {
        let lists = JSON.parse(saved)
        lists = lists.filter((list) => list.id !== listId)
        localStorage.setItem("triptoi-my-shared-lists", JSON.stringify(lists))
        setMySharedLists(lists)
      }
    } catch (error) {
      console.error("Error deleting shared list:", error)
    }
  }

  const handleUrlSubmit = async (e) => {
    e.preventDefault()
    if (!url.trim()) return

    setIsLoading(true)
    setIsSharedList(false)

    console.log("🔍 Submitting URL:", url.trim())

    try {
      const response = await fetch("/api/parse-maps.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ url: url.trim() }),
      })

      const data = await response.json()

      if (data.success && data.places.length > 0) {
        setPlaces(data.places)
        setAllVotes({})
        setMyVotes({})
        setLikedPlaces([])
        setCurrentView("map")
        setCurrentIndex(0)

        // ENSURE originalUrl is set IMMEDIATELY
        const submittedUrl = url.trim()
        setOriginalUrl(submittedUrl)

        console.log("✅ Original URL set to:", submittedUrl)

        const listInfo = {
          id: `created_${Date.now()}`,
          title: `Google Maps List (${data.places.length} places)`,
          places: data.places,
          votes: {},
          isShared: false,
          lastAccessed: Date.now(),
          preview: data.places
            .slice(0, 3)
            .map((p) => p.name)
            .join(", "),
          originalUrl: submittedUrl, // ENSURE it's in listInfo too
        }
        setCurrentListInfo(listInfo)
        saveListToRecents(listInfo)
      } else {
        // Handle demo case - no original URL
        const demoPlaces = getDemoPlaces()
        setPlaces(demoPlaces)
        setAllVotes({})
        setMyVotes({})
        setLikedPlaces([])
        setCurrentView("swipe")
        setCurrentIndex(0)
        setOriginalUrl("") // Clear for demo

        const listInfo = {
          id: `demo_${Date.now()}`,
          title: "Demo: Paris Attractions",
          places: demoPlaces,
          votes: {},
          isShared: false,
          lastAccessed: Date.now(),
          preview: demoPlaces
            .slice(0, 3)
            .map((p) => p.name)
            .join(", "),
        }
        setCurrentListInfo(listInfo)
        saveListToRecents(listInfo)
      }
    } catch (error) {
      console.error("Error:", error)
      // Handle error case - demo places, no original URL
      const demoPlaces = getDemoPlaces()
      setPlaces(demoPlaces)
      setAllVotes({})
      setMyVotes({})
      setLikedPlaces([])
      setCurrentView("swipe")
      setCurrentIndex(0)
      setOriginalUrl("") // Clear for demo
    } finally {
      setIsLoading(false)
    }
  }

  const handleDemo = () => {
    const demoPlaces = getDemoPlaces()
    setPlaces(demoPlaces)
    setAllVotes({})
    setMyVotes({})
    setLikedPlaces([])
    setIsSharedList(false)
    setCurrentView("map")
    setCurrentIndex(0)
    setOriginalUrl("")

    const listInfo = {
      id: `demo_${Date.now()}`,
      title: "Demo: Paris Attractions",
      places: demoPlaces,
      votes: {},
      isShared: false,
      lastAccessed: Date.now(),
      preview: demoPlaces
        .slice(0, 3)
        .map((p) => p.name)
        .join(", "),
    }
    setCurrentListInfo(listInfo)
    saveListToRecents(listInfo)
  }

  // Handle left/right side clicks for navigation
  const handleSideClick = (side) => {
    const filteredPlaces = getFilteredPlaces(places)
    if (side === "left" && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
    } else if (side === "right" && currentIndex < filteredPlaces.length - 1) {
      setCurrentIndex(currentIndex + 1)
    }
  }

  // Handle Netflix-style voting
  const handleVoteChange = async (voteType) => {
    const filteredPlaces = getFilteredPlaces(places)
    const currentPlace = filteredPlaces[currentIndex]
    const placeKey = `${currentPlace?.lat},${currentPlace?.lng}`

    console.log(`🗳️ Voting ${voteType} for ${currentPlace.name}`)
    setIsVoteSyncing(true)

    const newMyVotes = { ...myVotes }
    newMyVotes[placeKey] = {
      vote: voteType,
      userId: userId,
      timestamp: Date.now(),
    }
    setMyVotes(newMyVotes)

    const newAllVotes = { ...allVotes }
    if (!newAllVotes[placeKey]) {
      newAllVotes[placeKey] = { likes: 0, loves: 0, skips: 0, total: 0, users: {} }
    }

    const previousVote = newAllVotes[placeKey].users[userId]
    if (previousVote) {
      if (previousVote === "like") newAllVotes[placeKey].likes -= 1
      if (previousVote === "love") newAllVotes[placeKey].loves -= 1
      if (previousVote === "skip") newAllVotes[placeKey].skips -= 1
      newAllVotes[placeKey].total -= 1
    }

    if (voteType === "like") {
      newAllVotes[placeKey].likes += 1
      if (!likedPlaces.find((p) => p.id === currentPlace.id)) {
        setLikedPlaces((prev) => [...prev, currentPlace])
      }
    } else if (voteType === "love") {
      newAllVotes[placeKey].loves += 1
      if (!likedPlaces.find((p) => p.id === currentPlace.id)) {
        setLikedPlaces((prev) => [...prev, currentPlace])
      }
    } else if (voteType === "skip") {
      newAllVotes[placeKey].skips += 1
      setLikedPlaces((prev) => prev.filter((p) => p.id !== currentPlace.id))
    }

    newAllVotes[placeKey].total += 1
    newAllVotes[placeKey].users[userId] = voteType

    if (!newAllVotes[placeKey].userDetails) {
      newAllVotes[placeKey].userDetails = []
    }

    newAllVotes[placeKey].userDetails = newAllVotes[placeKey].userDetails.filter((detail) => detail.userId !== userId)

    newAllVotes[placeKey].userDetails.push({
      userId: userId,
      nickname: userNickname || `User ${userId.slice(-4)}`,
      vote: voteType,
      votedAt: new Date().toISOString(),
    })

    setAllVotes(newAllVotes)

    if (currentListId) {
      try {
        console.log(`📤 Sending vote to server: ${voteType}`)
        const response = await fetch(`${API_BASE}/list-manager.php?action=vote`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            listId: currentListId,
            placeKey,
            userId,
            voteType,
          }),
        })

        const data = await response.json()
        if (data.success) {
          console.log(`✅ Vote sent successfully: ${voteType}`)
        } else {
          console.error("Failed to submit vote:", data.error)
        }
      } catch (error) {
        console.error("Error submitting vote:", error)
      }
    }

    setIsVoteSyncing(false)

    if (currentListInfo) {
      const updatedListInfo = {
        ...currentListInfo,
        votes: newAllVotes,
        lastAccessed: Date.now(),
      }
      setCurrentListInfo(updatedListInfo)
      saveListToRecents(updatedListInfo)
    }
  }

  const handleNoteChange = async (noteText) => {
    const filteredPlaces = getFilteredPlaces(places)
    const currentPlace = filteredPlaces[currentIndex]
    const placeKey = `${currentPlace?.lat},${currentPlace?.lng}`

    console.log(`💭 Adding note for ${currentPlace.name}: "${noteText}"`)

    // Update local notes
    const newMyNotes = { ...myNotes }
    if (noteText.trim()) {
      newMyNotes[placeKey] = {
        note: noteText.trim(),
        userId: userId,
        timestamp: Date.now(),
      }
    } else {
      delete newMyNotes[placeKey] // Remove note if empty
    }
    setMyNotes(newMyNotes)

    // Update vote data to include the note
    const newAllVotes = { ...allVotes }
    if (!newAllVotes[placeKey]) {
      newAllVotes[placeKey] = { likes: 0, loves: 0, skips: 0, total: 0, users: {}, userDetails: [] }
    }

    if (!newAllVotes[placeKey].userDetails) {
      newAllVotes[placeKey].userDetails = []
    }

    // Find and update user's details
    const userDetailIndex = newAllVotes[placeKey].userDetails.findIndex((detail) => detail.userId === userId)

    if (userDetailIndex >= 0) {
      // Update existing user detail
      if (noteText.trim()) {
        newAllVotes[placeKey].userDetails[userDetailIndex].note = noteText.trim()
      } else {
        delete newAllVotes[placeKey].userDetails[userDetailIndex].note
      }
    } else if (noteText.trim()) {
      // Add new user detail with note (if user hasn't voted yet)
      newAllVotes[placeKey].userDetails.push({
        userId: userId,
        nickname: userNickname || `User ${userId.slice(-4)}`,
        note: noteText.trim(),
        votedAt: new Date().toISOString(),
      })
    }

    setAllVotes(newAllVotes)

    // Auto-create shared list if needed, then sync to server
    if (!currentListId && noteText.trim()) {
      // Auto-create a shared list when first note is added
      await generateShareLink()
    }

    // Sync to server if shared list
    if (currentListId) {
      try {
        const response = await fetch(`${API_BASE}/list-manager.php?action=note`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            listId: currentListId,
            placeKey,
            userId,
            note: noteText.trim() || null,
          }),
        })

        const data = await response.json()
        if (data.success) {
          console.log(`✅ Note synced successfully`)
        } else {
          console.error("Failed to sync note:", data.error)
        }
      } catch (error) {
        console.error("Error syncing note:", error)
      }
    }
  }

  const handleTypeChange = async (placeKey, newType) => {
    setPlaceTypes((prev) => ({
      ...prev,
      [placeKey]: newType,
    }))

    // Save to localStorage
    const savedTypes = JSON.parse(localStorage.getItem("triptoi-place-types") || "{}")
    savedTypes[placeKey] = newType
    localStorage.setItem("triptoi-place-types", JSON.stringify(savedTypes))

    console.log(`🏷️ Set type for ${placeKey}: ${newType}`)

    // Only try to sync to server if we have a shared list
    if (isSharedList && currentListId) {
      // Cancel any existing type update request
      if (updateRequestRef.current) {
        console.log("🚫 Cancelling previous type update request")
        updateRequestRef.current.abort()
      }

      // Clear any existing debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }

      // Debounce the server request by 1 second
      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          console.log("🔄 Syncing place type to database:", { placeKey, newType, currentListId, userId })

          // Create an AbortController for this request
          const controller = new AbortController()
          updateRequestRef.current = controller

          const response = await fetch(`${API_BASE}/list-manager.php?action=update-place-type`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              listId: currentListId,
              placeKey: placeKey,
              newType: newType,
              userId: userId,
            }),
            signal: controller.signal,
          })

          console.log("📥 Type update response status:", response.status, response.statusText)

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }

          const responseText = await response.text()
          console.log("📥 Type update raw response:", responseText)

          let data
          try {
            data = JSON.parse(responseText)
          } catch (parseError) {
            console.error("❌ Failed to parse type update JSON response:", parseError)
            console.error("❌ Raw response was:", responseText)
            throw new Error("Invalid response format from server")
          }

          console.log("📥 Type update parsed response:", data)

          if (data.success === true || data.status === "success") {
            console.log("✅ Place type synced to database successfully")

            // If server returns updated place types, use them
            if (data.placeTypes) {
              console.log("🔄 Server returned updated place types:", data.placeTypes)
              setPlaceTypes(data.placeTypes)
              localStorage.setItem("triptoi-place-types", JSON.stringify(data.placeTypes))
            }
          } else {
            throw new Error(data.error || data.message || "Failed to update place type")
          }
        } catch (error) {
          if (error.name === "AbortError") {
            console.log("🚫 Type update request was cancelled")
            return
          }
          console.error("❌ Error syncing place type:", error)
          console.log("💾 Place type saved locally, but server sync failed")

          // Show user that sync failed but local save worked
          console.warn("⚠️ Place type saved locally but couldn't sync to server. Other users won't see this change.")
        } finally {
          // Clear the request reference
          updateRequestRef.current = null
        }
      }, 1000) // 1 second debounce
    } else {
      console.log("💾 Place type saved locally (no shared list to sync to)")
    }
  }

  const handlePlaceUpdate = async (placeId, updatedPlace) => {
    // Validate the updated place name
    if (!updatedPlace.name || updatedPlace.name.trim() === "") {
      alert("Place name cannot be empty")
      return
    }

    const originalPlace = places.find((p) => p.id === placeId)
    if (!originalPlace) {
      alert("Place not found")
      return
    }

    // Clean the name - remove special characters that might cause issues
    const cleanedName = updatedPlace.name.trim().replace(/[<>"'&]/g, "")

    if (cleanedName !== updatedPlace.name) {
      console.log("Cleaned place name:", cleanedName)
    }

    // Update local state immediately for UI responsiveness
    setPlaces((prev) => prev.map((p) => (p.id === placeId ? { ...p, name: cleanedName } : p)))
    setLikedPlaces((prev) => prev.map((p) => (p.id === placeId ? { ...p, name: cleanedName } : p)))

    // Save to localStorage for local persistence
    const savedEdits = JSON.parse(localStorage.getItem("triptoi-edits") || "{}")
    const placeKey = `${originalPlace.lat},${originalPlace.lng}`
    savedEdits[placeKey] = cleanedName
    localStorage.setItem("triptoi-edits", JSON.stringify(savedEdits))

    // Only try to sync to server if we have a shared list
    if (isSharedList && currentListId) {
      // Cancel any existing request
      if (updateRequestRef.current) {
        console.log("🚫 Cancelling previous update request")
        updateRequestRef.current.abort()
      }

      // Clear any existing debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }

      // Debounce the server request by 1 second
      debounceTimeoutRef.current = setTimeout(async () => {
        try {
          console.log("🔄 Syncing place name to database:", cleanedName)

          // Create an AbortController for this request
          const controller = new AbortController()
          updateRequestRef.current = controller

          const response = await fetch(`${API_BASE}/list-manager.php?action=update-place-name`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              listId: currentListId,
              placeKey: placeKey,
              newName: cleanedName,
              userId: userId,
            }),
            signal: controller.signal, // Add abort signal
          })

          console.log("📥 Response status:", response.status, response.statusText)

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`)
          }

          const responseText = await response.text()
          console.log("📥 Raw response:", responseText)

          let data
          try {
            data = JSON.parse(responseText)
          } catch (parseError) {
            console.error("❌ Failed to parse JSON response:", parseError)
            throw new Error("Invalid response format from server")
          }

          console.log("📥 Parsed response:", data)

          if (data.success === true || data.status === "success") {
            console.log("✅ Place name synced to database successfully")

            // Update currentListInfo if it exists
            if (currentListInfo) {
              const updatedListInfo = {
                ...currentListInfo,
                places: currentListInfo.places.map((p) => (p.id === placeId ? { ...p, name: cleanedName } : p)),
                lastAccessed: Date.now(),
              }
              setCurrentListInfo(updatedListInfo)
              saveListToRecents(updatedListInfo)
            }
          } else {
            throw new Error(data.error || data.message || "Failed to update place name")
          }
        } catch (error) {
          if (error.name === "AbortError") {
            console.log("🚫 Request was cancelled")
            return
          }
          console.error("❌ Error syncing place name:", error)
          // Don't revert local state - keep the local edit even if server sync fails
          console.log("💾 Place name saved locally, but server sync failed")
        } finally {
          // Clear the request reference
          updateRequestRef.current = null
        }
      }, 1000) // 1 second debounce
    } else {
      console.log("💾 Place name saved locally (no shared list to sync to)")
    }
  }

  const handleReset = () => {
    setCurrentView("input")
    setPlaces([])
    setCurrentIndex(0)
    setLikedPlaces([])
    setMyVotes({})
    setAllVotes({})
    setIsSharedList(false)
    setUrl("")
    setCurrentListInfo(null)
    setCurrentListId(null)
    setIsCreator(false)
    setOriginalUrl("")
    setLinkedTripData(null)
    setHasLinkedTrip(false)
    setSharedPlanId(null)
    window.history.replaceState({}, document.title, window.location.pathname)
  }

  const generateShareLink = async () => {
    console.log("🔍 generateShareLink called with originalUrl:", originalUrl)
    console.log("🔍 currentListInfo originalUrl:", currentListInfo?.originalUrl)

    try {
      setIsLoading(true)

      if (currentListId) {
        const existingUrl = `https://triptoi.com/share/${currentListId}`

        const uniqueVoters = new Set()
        Object.values(allVotes).forEach((vote) => {
          Object.keys(vote.users || {}).forEach((userId) => uniqueVoters.add(userId))
        })

        const tripPlanText = hasLinkedTrip ? "\n\n📅 This list has a linked trip plan!" : ""

        navigator.clipboard
          .writeText(existingUrl)
          .then(() => {
            alert(
              `✅ Share link copied to clipboard!\n\n${existingUrl}\n\n👥 This list already has votes from ${uniqueVoters.size} people!\n\n🔄 Votes sync in real-time!${isCreator ? "\n\n👑 You are the creator - you can clear votes anytime!" : ""}${tripPlanText}`,
            )
          })
          .catch(() => {
            prompt("Copy this share link:", existingUrl)
          })

        return
      }

      const cleanPlaces = places.map((place) => ({
        id: place.id,
        name: place.name.replace(/[^\w\s-]/g, ""),
        lat: place.lat,
        lng: place.lng,
        isManuallyAdded: place.isManuallyAdded || false,
      }))

      const password = prompt("Set an optional password for this list (leave empty for no password):")

      const requestBody = {
        title: `Travel List (${cleanPlaces.length} places)`,
        places: cleanPlaces,
        password: password || undefined,
        creatorUserId: userId,
        originalUrl: originalUrl || undefined,
      }

      console.log("🔍 Sending request body:", requestBody)

      const response = await fetch(`${API_BASE}/list-manager.php?action=create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      })

      const data = await response.json()

      if (data.success) {
        setCurrentListId(data.id)
        setIsSharedList(true)
        setIsCreator(true)

        const sharedListData = {
          id: data.id,
          title: `Travel List (${cleanPlaces.length} places)`,
          url: data.url,
          places: cleanPlaces,
          hasPassword: data.hasPassword,
          createdAt: Date.now(),
          lastAccessed: Date.now(),
          totalPlaces: cleanPlaces.length,
          originalUrl: originalUrl,
          preview: cleanPlaces
            .slice(0, 3)
            .map((p) => p.name)
            .join(", "),
        }
        saveSharedList(sharedListData)

        navigator.clipboard
          .writeText(data.url)
          .then(() => {
            alert(
              `✅ Share link created and copied to clipboard!\n\n${data.url}${data.hasPassword ? "\n\n🔒 This list is password protected." : ""}\n\n👥 Share this link so others can vote!\n🔄 Votes sync in real-time!\n\n👑 You are the creator - you can clear votes anytime!`,
            )
          })
          .catch(() => {
            prompt("Copy this share link:", data.url)
          })
      } else {
        throw new Error(data.error || "Failed to create share link")
      }
    } catch (error) {
      console.error("Share error:", error)
      alert("Error creating share link: " + error.message)
    } finally {
      setIsLoading(false)
    }
  }

  // Handle map view
  const handleShowMap = () => {
    setCurrentView("map")
    setShowManageMenu(false)
  }

  // Handle starting voting from map
  const handleStartVotingFromMap = (selectedPlaces) => {
    setPlaces(selectedPlaces)
    setCurrentView("swipe")
    setShowManageMenu(false)

    // Check if there's a target place index from the map popup
    const targetIndex = localStorage.getItem("triptoi-target-place-index")
    if (targetIndex !== null) {
      const index = Number.parseInt(targetIndex, 10)
      if (index >= 0 && index < selectedPlaces.length) {
        setCurrentIndex(index)
      } else {
        setCurrentIndex(0)
      }
      // Clean up the stored index
      localStorage.removeItem("triptoi-target-place-index")
    } else {
      setCurrentIndex(0)
    }
  }

  // Handle back to map from voting
  const handleBackToMap = () => {
    setCurrentView("map")
  }

  // Handle back to swipe from map
  const handleBackToSwipe = () => {
    setCurrentView("swipe")
    setShowManageMenu(false)
  }

  const getDemoPlaces = () => [
    {
      id: "1",
      name: "Eiffel Tower",
      lat: 48.8584,
      lng: 2.2945,
    },
    {
      id: "2",
      name: "Louvre Museum",
      lat: 48.8606,
      lng: 2.3376,
    },
    {
      id: "3",
      name: "Arc de Triomphe",
      lat: 48.8738,
      lng: 2.295,
    },
    {
      id: "4",
      name: "Notre-Dame Cathedral",
      lat: 48.853,
      lng: 2.3499,
    },
    {
      id: "5",
      name: "Sacre-Coeur Basilica",
      lat: 48.8867,
      lng: 2.3431,
    },
    {
      id: "6",
      name: "Champs-Elysees",
      lat: 48.8698,
      lng: 2.3076,
    },
  ]

  const formatTimeAgo = (timestamp) => {
    const now = Date.now()
    const diff = now - timestamp
    const minutes = Math.floor(diff / (1000 * 60))
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (minutes < 1) return "Just now"
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return new Date(timestamp).toLocaleDateString()
  }

  // Add keyboard navigation for swipe view
  useEffect(() => {
    const handleKeyPress = (event) => {
      if (currentView !== "swipe") return

      if (event.key === "ArrowLeft" && currentIndex > 0) {
        setCurrentIndex(currentIndex - 1)
      } else if (event.key === "ArrowRight") {
        if (currentIndex < places.length - 1) {
          setCurrentIndex(currentIndex + 1)
        }
      }
    }

    if (currentView === "swipe") {
      window.addEventListener("keydown", handleKeyPress)
    }

    return () => {
      window.removeEventListener("keydown", handleKeyPress)
    }
  }, [currentView, currentIndex, places.length])

  // Close manage menu when view changes
  useEffect(() => {
    setShowManageMenu(false)
  }, [currentView])

  // Add this cleanup effect
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (updateRequestRef.current) {
        updateRequestRef.current.abort()
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current)
      }
    }
  }, [])

  // Add planning view to the main render logic
  if (currentView === "planning") {
    return (
      <div className="app">
        <PlanningInterface
          places={places}
          currentListId={currentListId}
          userId={userId}
          userNickname={userNickname}
          onBackToMap={handleBackToMap}
          onBackToVoting={handleBackFromPlanning}
          API_BASE={API_BASE}
          linkedTripData={linkedTripData}
          onTripDataUpdate={isSharedList ? updateSharedTripData : saveLinkedTripData}
        />
      </div>
    )
  }

  if (currentView === "input") {
    return (
      <div className="app">
        <div className="container">
          <div className="input-card">
            <div className="logo">
              <img src="/triptoi-logo.png" alt="Triptoi" />
            </div>
            <h1>Share it. Vote it. Love it.</h1>
            <p>
              Got a Google Maps list of places? Paste the link below to let your group vote and help decide where to go
              next.
            </p>

            <form onSubmit={handleUrlSubmit}>
              <input
                type="url"
                placeholder="https://maps.google.com/maps/..."
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                disabled={isLoading}
              />

              <button type="submit" disabled={!url.trim() || isLoading}>
                {isLoading ? "Processing..." : "🗺️ Create List"}
              </button>
            </form>

            <div className="divider">
              <span>Or</span>
            </div>

            <button className="demo-btn" onClick={handleDemo} disabled={isLoading}>
              Try Demo with Paris Attractions
            </button>

            {/* Recent Lists Section */}
            {savedLists.length > 0 && (
              <div className="recent-lists">
                <h3>📂 Recent Lists</h3>
                <div className="lists-grid">
                  {savedLists.map((list) => (
                    <div key={list.id} className="list-card">
                      <div className="list-header">
                        <h4>{list.title}</h4>
                        <button onClick={() => deleteSavedList(list.id)} className="delete-btn" title="Delete list">
                          ✕
                        </button>
                      </div>
                      <p className="list-preview">{list.preview}</p>
                      <div className="list-meta">
                        <span className="list-count">{list.places.length} places</span>
                        <span className="list-time">{formatTimeAgo(list.lastAccessed)}</span>
                        {list.isShared && <span className="shared-badge">👥 Shared</span>}
                      </div>
                      <button onClick={() => loadSavedList(list)} className="load-list-btn">
                        Open List
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* My Shared Lists Section */}
            {mySharedLists.length > 0 && (
              <div className="recent-lists">
                <h3>🔗 My Shared Lists</h3>
                <div className="lists-grid">
                  {mySharedLists.map((list) => (
                    <div key={list.id} className="list-card shared-list-card">
                      <div className="list-header">
                        <h4>{list.title}</h4>
                        <button
                          onClick={() => deleteMySharedList(list.id)}
                          className="delete-btn"
                          title="Delete shared list"
                        >
                          ✕
                        </button>
                      </div>
                      <p className="list-preview">{list.preview}</p>
                      <div className="list-meta">
                        <span className="list-count">{list.totalPlaces} places</span>
                        <span className="list-time">{formatTimeAgo(list.lastAccessed)}</span>
                        <span className="shared-badge">👥 Live</span>
                        {list.hasPassword && <span className="password-badge">🔒</span>}
                      </div>
                      <div className="shared-list-actions">
                        <button onClick={() => loadMySharedList(list)} className="load-list-btn">
                          Open Live List
                        </button>
                        <button
                          onClick={() => navigator.clipboard.writeText(list.url).then(() => alert("Link copied!"))}
                          className="copy-link-btn"
                        >
                          📋 Copy Link
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="note">
              <p>💡 Browse places and vote with Netflix-style thumbs!</p>
              <p>🔗 Share links work across all devices and browsers</p>
              <p>🔄 Votes sync in real-time when shared!</p>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (currentView === "map") {
    const totalVotes = Object.values(allVotes).reduce((sum, vote) => sum + vote.total, 0)
    const uniqueVoters = new Set()
    Object.values(allVotes).forEach((vote) => {
      Object.keys(vote.users || {}).forEach((userId) => uniqueVoters.add(userId))
    })

    return (
      <div className="app swipe-view">
        <div className="header">
          <div className="header-left">
            <button onClick={handleBackToSwipe} className="btn-view-toggle primary" title="Switch to card view">
              <MapPin size={18} />
              <span className="btn-text">Cards</span>
            </button>
            <TypeFilter
              activeFilter={typeFilter}
              onFilterChange={setTypeFilter}
              places={places}
              placeTypes={placeTypes}
            />
          </div>
          <div className="header-right">
            {/* Add Calendar button */}
            <button
              onClick={handlePlanningModalOpen}
              className="btn-calendar"
              title="Plan your trip"
              style={{
                background: hasLinkedTrip ? "#4CAF50" : "#2196F3",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginRight: "8px",
              }}
            >
              📅 {hasLinkedTrip ? "Edit Plan" : "Plan Trip"}
              {hasLinkedTrip && isSharedList && <span style={{ fontSize: "10px", opacity: 0.8 }}>👥</span>}
            </button>
            <button
              onClick={() => setShowManageMenu(true)}
              className="btn-settings"
              title="Manage sharing and settings"
            >
              <Menu size={28} />
            </button>
          </div>
        </div>

        <MapView
          places={getFilteredPlaces(places)}
          allVotes={allVotes}
          onStartVoting={handleStartVotingFromMap}
          onBackToSwipe={handleBackToSwipe}
          showVotes={showVotes}
          placeTypes={placeTypes}
          onAddLocation={handleAddLocation}
        />

        {/* Include the same manage menu as swipe view */}
        {showManageMenu && (
          <div className="manage-overlay">
            <div className="manage-backdrop" onClick={() => setShowManageMenu(false)} />
            <div className="manage-sidebar">
              <div className="manage-header">
                <h2>Manage Sharing</h2>
                <p>Control your list settings and sharing options</p>
                <p>
                  {isSharedList && (
                    <span
                      className="shared-indicator"
                      title={lastSyncTime ? `Last synced: ${formatTimeAgo(lastSyncTime)}` : ""}
                    >
                      👥 {isVoteSyncing ? "🔄" : "Live"} {isCreator && "👑"}
                      {hasLinkedTrip && " 📅"}
                    </span>
                  )}
                </p>
              </div>

              <div className="manage-stats">
                <div className="stat-card">
                  <span className="stat-number">{likedPlaces.length}</span>
                  <span className="stat-label">Your Likes</span>
                </div>
                <div className="stat-card">
                  <span className="stat-number">{totalVotes}</span>
                  <span className="stat-label">Total Votes</span>
                </div>
                <div className="stat-card">
                  <span className="stat-number">{uniqueVoters.size}</span>
                  <span className="stat-label">Voters</span>
                </div>
              </div>

              <div className="manage-sections">
                {/* Personal Settings */}
                <div className="manage-section">
                  <div className="section-header">
                    <User size={20} />
                    <h3>Personal Settings</h3>
                  </div>
                  <button onClick={() => setShowNicknameModal(true)} className="manage-option">
                    <div className="option-content">
                      <span className="option-title">Display Name</span>
                      <span className="option-subtitle">{userNickname || "Not set"}</span>
                    </div>
                    <ArrowLeft size={16} className="option-arrow" />
                  </button>

                  {isSharedList && (
                    <button onClick={() => setShowVoteRecoveryModal(true)} className="manage-option">
                      <div className="option-content">
                        <span className="option-title">Reclaim Previous Votes</span>
                        <span className="option-subtitle">Continue voting from another device</span>
                      </div>
                      <User size={16} className="option-arrow" />
                    </button>
                  )}

                  <button onClick={handleReset} className="manage-option danger">
                    <div className="option-content">
                      <span className="option-title">Start Over</span>
                      <span className="option-subtitle">Return to home screen and create a new list</span>
                    </div>
                    <RotateCcw size={16} className="option-icon" />
                  </button>
                </div>

                {/* Sharing Options */}
                <div className="manage-section">
                  <div className="section-header">
                    <Share2 size={20} />
                    <h3>Sharing Options</h3>
                  </div>
                  <button onClick={generateShareLink} className="manage-option">
                    <div className="option-content">
                      <span className="option-title">{currentListId ? "Copy Share Link" : "Create Share Link"}</span>
                      {currentListId && (
                        <span className="option-subtitle">
                          Share this link so others can vote{hasLinkedTrip ? " and plan together" : ""}
                        </span>
                      )}
                    </div>
                    <ArrowLeft size={16} className="option-arrow" />
                  </button>

                  {places.length > 0 && (
                    <button onClick={handleRefreshFromUrl} className="manage-option">
                      <div className="option-content">
                        <span className="option-title">Refresh from URL</span>
                        <span className="option-subtitle">
                          {originalUrl || currentListInfo?.originalUrl
                            ? "Update places from original Google Maps link"
                            : "Refresh list (requires original URL)"}
                        </span>
                      </div>
                      <RefreshCw size={16} className="option-icon" />
                    </button>
                  )}
                </div>

                {/* Creator Controls */}
                {isCreator && isSharedList && (
                  <div className="manage-section">
                    <div className="section-header">
                      <Crown size={20} />
                      <h3>Creator Controls</h3>
                    </div>

                    <button onClick={handleClearVotes} className="manage-option danger">
                      <div className="option-content">
                        <span className="option-title">Clear All Votes</span>
                        <span className="option-subtitle">Permanently delete all votes from everyone</span>
                      </div>
                      <Trash2 size={16} className="option-icon" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Include the same modals */}
        <NicknameModal
          isOpen={showNicknameModal}
          currentNickname={userNickname}
          onSave={saveUserNickname}
          onClose={() => setShowNicknameModal(false)}
        />

        <PasswordModal
          isOpen={showPasswordModal}
          onSubmit={handlePasswordSubmit}
          onClose={() => {
            setShowPasswordModal(false)
            window.history.replaceState({}, document.title, window.location.pathname)
          }}
          isLoading={isPasswordLoading}
          error={passwordError}
        />

        <VoteRecoveryModal
          isOpen={showVoteRecoveryModal}
          onClose={() => setShowVoteRecoveryModal(false)}
          onRecoverVotes={handleRecoverVotes}
          allVotes={allVotes}
          places={places}
          isLoading={isRecoveryLoading}
        />

        {/* Add Planning Modal */}
        <PlanningModal
          isOpen={showPlanningModal}
          onClose={handlePlanningModalClose}
          places={places}
          allVotes={allVotes}
          linkedTripData={linkedTripData}
          hasLinkedTrip={hasLinkedTrip}
          onTripCreated={handleSwitchToPlanningView} // This should call the switch function
          userId={userId}
          currentListId={currentListId}
          userNickname={userNickname}
          API_BASE={API_BASE}
        />
      </div>
    )
  }

  if (currentView === "swipe") {
    const filteredPlaces = getFilteredPlaces(places)
    const currentPlace = filteredPlaces[currentIndex]
    const placeKey = `${currentPlace?.lat},${currentPlace?.lng}`
    const totalVotes = Object.values(allVotes).reduce((sum, vote) => sum + vote.total, 0)
    const uniqueVoters = new Set()
    Object.values(allVotes).forEach((vote) => {
      Object.keys(vote.users || {}).forEach((userId) => uniqueVoters.add(userId))
    })

    return (
      <div className="app swipe-view">
        <div className="header">
          <div className="header-left">
            <button onClick={handleShowMap} className="btn-view-toggle primary" title="Switch to map view">
              <Map size={18} />
              <span className="btn-text">Map</span>
            </button>
            <TypeFilter
              activeFilter={typeFilter}
              onFilterChange={setTypeFilter}
              places={places}
              placeTypes={placeTypes}
            />
          </div>
          <div className="header-right">
            <button
              onClick={() => setShowVotes(!showVotes)}
              className={`toggle-votes-btn ${showVotes ? "active" : ""}`}
              title="Toggle vote visibility"
            >
              {showVotes ? <EyeOff size={16} /> : <Eye size={16} />}
              <span className="btn-text">{showVotes ? "Hide" : "Show"}</span>
            </button>

            {/* Add Calendar button */}
            <button
              onClick={handlePlanningModalOpen}
              className="btn-calendar"
              title="Plan your trip"
              style={{
                background: hasLinkedTrip ? "#4CAF50" : "#2196F3",
                color: "white",
                border: "none",
                borderRadius: "8px",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                marginRight: "8px",
              }}
            >
              📅 {hasLinkedTrip ? "Edit Plan" : "Plan Trip"}
              {hasLinkedTrip && isSharedList && <span style={{ fontSize: "10px", opacity: 0.8 }}>👥</span>}
            </button>

            <button
              onClick={() => setShowManageMenu(true)}
              className="btn-settings"
              title="Manage sharing and settings"
            >
              <Menu size={28} />
            </button>
          </div>
        </div>

        <div className="cards-container">
          {getFilteredPlaces(places)
            .slice(currentIndex, currentIndex + 1)
            .map((place, index) => {
              return (
                <PlaceCard
                  key={place.id}
                  place={place}
                  isActive={index === 0}
                  onPlaceUpdate={handlePlaceUpdate}
                  voteData={allVotes[placeKey]}
                  myVote={myVotes[placeKey]}
                  onSideClick={handleSideClick}
                  currentIndex={currentIndex}
                  totalPlaces={getFilteredPlaces(places).length}
                  allVotes={allVotes}
                  userId={userId}
                  showVotes={showVotes}
                  onVoteChange={handleVoteChange}
                  isLastItem={currentIndex === getFilteredPlaces(places).length - 1}
                  placeType={placeTypes[placeKey]}
                  onTypeChange={handleTypeChange}
                  onNoteChange={handleNoteChange}
                />
              )
            })}
        </div>

        {/* Sticky Bottom Navigation */}
        <div className="bottom-navigation">
          <div
            className={`nav-click-area left ${currentIndex === 0 ? "disabled" : ""}`}
            onClick={() => currentIndex > 0 && handleSideClick("left")}
          >
            <button className="nav-arrow-btn" aria-label="Previous place">
              ‹
            </button>
          </div>

          <div
            className={`nav-click-area right ${currentIndex === getFilteredPlaces(places).length - 1 ? "disabled" : ""}`}
            onClick={() => handleSideClick("right")}
          >
            <button className="nav-arrow-btn" aria-label="Next place">
              ›
            </button>
          </div>

          <div className="bottom-progress-clean">
            <div className="progress-text-clean">
              {currentIndex + 1} / {getFilteredPlaces(places).length}
            </div>
          </div>
        </div>

        {/* Manage menu - only render when showManageMenu is true */}
        {showManageMenu && (
          <div className="manage-overlay">
            <div className="manage-backdrop" onClick={() => setShowManageMenu(false)} />
            <div className="manage-sidebar">
              <div className="manage-header">
                <h2>Manage Sharing</h2>
                <p>Control your list settings and sharing options</p>
              </div>
              <p>
                {isSharedList && (
                  <span
                    className="shared-indicator"
                    title={lastSyncTime ? `Last synced: ${formatTimeAgo(lastSyncTime)}` : ""}
                  >
                    👥 {isVoteSyncing ? "🔄" : "Live"} {isCreator && "👑"}
                    {hasLinkedTrip && " 📅"}
                  </span>
                )}
              </p>

              <div className="manage-stats">
                <div className="stat-card">
                  <span className="stat-number">{likedPlaces.length}</span>
                  <span className="stat-label">Your Likes</span>
                </div>
                <div className="stat-card">
                  <span className="stat-number">{totalVotes}</span>
                  <span className="stat-label">Total Votes</span>
                </div>
                <div className="stat-card">
                  <span className="stat-number">{uniqueVoters.size}</span>
                  <span className="stat-label">Voters</span>
                </div>
              </div>

              <div className="manage-sections">
                {/* Personal Settings */}
                <div className="manage-section">
                  <div className="section-header">
                    <User size={20} />
                    <h3>Personal Settings</h3>
                  </div>
                  <button onClick={() => setShowNicknameModal(true)} className="manage-option">
                    <div className="option-content">
                      <span className="option-title">Display Name</span>
                      <span className="option-subtitle">{userNickname || "Not set"}</span>
                    </div>
                    <ArrowLeft size={16} className="option-arrow" />
                  </button>

                  {isSharedList && (
                    <button onClick={() => setShowVoteRecoveryModal(true)} className="manage-option">
                      <div className="option-content">
                        <span className="option-title">Reclaim Previous Votes</span>
                        <span className="option-subtitle">Continue voting from another device</span>
                      </div>
                      <User size={16} className="option-arrow" />
                    </button>
                  )}

                  <button onClick={handleReset} className="manage-option danger">
                    <div className="option-content">
                      <span className="option-title">Start Over</span>
                      <span className="option-subtitle">Return to home screen and create a new list</span>
                    </div>
                    <RotateCcw size={16} className="option-icon" />
                  </button>
                </div>

                {/* Sharing Options */}
                <div className="manage-section">
                  <div className="section-header">
                    <Share2 size={20} />
                    <h3>Sharing Options</h3>
                  </div>
                  <button onClick={generateShareLink} className="manage-option">
                    <div className="option-content">
                      <span className="option-title">{currentListId ? "Copy Share Link" : "Create Share Link"}</span>
                      {currentListId && (
                        <span className="option-subtitle">
                          Share this link so others can vote{hasLinkedTrip ? " and plan together" : ""}
                        </span>
                      )}
                    </div>
                    <ArrowLeft size={16} className="option-arrow" />
                  </button>

                  {places.length > 0 && (
                    <button onClick={handleRefreshFromUrl} className="manage-option">
                      <div className="option-content">
                        <span className="option-title">Refresh from URL</span>
                        <span className="option-subtitle">
                          {originalUrl || currentListInfo?.originalUrl
                            ? "Update places from original Google Maps link"
                            : "Refresh list (requires original URL)"}
                        </span>
                      </div>
                      <RefreshCw size={16} className="option-icon" />
                    </button>
                  )}
                </div>

                {/* Creator Controls */}
                {isCreator && isSharedList && (
                  <div className="manage-section">
                    <div className="section-header">
                      <Crown size={20} />
                      <h3>Creator Controls</h3>
                    </div>

                    <button onClick={handleClearVotes} className="manage-option danger">
                      <div className="option-content">
                        <span className="option-title">Clear All Votes</span>
                        <span className="option-subtitle">Permanently delete all votes from everyone</span>
                      </div>
                      <Trash2 size={16} className="option-icon" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Include the same modals */}
        <NicknameModal
          isOpen={showNicknameModal}
          currentNickname={userNickname}
          onSave={saveUserNickname}
          onClose={() => setShowNicknameModal(false)}
        />

        <PasswordModal
          isOpen={showPasswordModal}
          onSubmit={handlePasswordSubmit}
          onClose={() => {
            setShowPasswordModal(false)
            window.history.replaceState({}, document.title, window.location.pathname)
          }}
          isLoading={isPasswordLoading}
          error={passwordError}
        />

        <VoteRecoveryModal
          isOpen={showVoteRecoveryModal}
          onClose={() => setShowVoteRecoveryModal(false)}
          onRecoverVotes={handleRecoverVotes}
          allVotes={allVotes}
          places={places}
          isLoading={isRecoveryLoading}
        />

        {/* Add Planning Modal */}
        <PlanningModal
          isOpen={showPlanningModal}
          onClose={handlePlanningModalClose}
          places={places}
          allVotes={allVotes}
          linkedTripData={linkedTripData}
          hasLinkedTrip={hasLinkedTrip}
          onTripCreated={handleSwitchToPlanningView} // This should call the switch function
          userId={userId}
          currentListId={currentListId}
          userNickname={userNickname}
          API_BASE={API_BASE}
        />
      </div>
    )
  }
}

export default App
